import { cx } from '@emotion/css';
import type { FC, MouseEventHandler, ReactEventHandler } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useIsMobile } from '../../../hooks';
import type { BaseComponentProps, ImageSources } from '../../../types';
import type { OnNavigateHandler } from '../../../types/activationEvents';
import { editorialGalleryBrandNames, type EditorialGalleryBrand } from '../../EditorialGalleryCard';
import { Icon } from '../../Icon';
import { Media } from '../../Media';
import { Picture } from '../../Picture';
import { PrimitivesContext } from '../../Primitives';
import { isVideoPlaying } from '../../Video';
import {
  arrowCss,
  bodyCss,
  bodyOnlyClampCss,
  cardCss,
  cardLandscapeWidthCss,
  cardPortraitWidthCss,
  cardResponsiveWidthCss,
  image11Css,
  image169Css,
  image32Css,
  imageCss,
  imageLoadingCss,
  infoElementCss,
  linkCss,
  mediaContainerCss,
  mediaLogoContainerCss,
  mediaLogoImageCss,
  mediaLogoImageMobileCss,
  mediaLogoPictureCss,
  mediaLogoPictureMobileCss,
  metadataContainerCss,
  metadataTextCss,
  relatedContentLineClampCss,
  singleViewLandscapeWidthCss,
  subtitleCss,
  subtitleOnlyClampCss,
  textCss,
  textOverlapCss,
  textPlayerControlsCss,
  textWrapperCss,
  textWrapperOverlapCss,
  titleCss,
  titleOnlyClampCss,
  videoCss,
  videoPlayerControlsCss,
} from './CarouselCardItem.styles';
import type { CarouselV3AspectRatios } from './types';

export interface CarouselCardItemProps extends BaseComponentProps {
  aspectRatio?: CarouselV3AspectRatios;
  brand?: EditorialGalleryBrand;
  date?: string;
  title?: string;
  subtitle?: string;
  body?: string;
  // click settings
  url?: string;
  openNewTab?: boolean;
  onClick?: OnNavigateHandler;
  // image settings
  imgSrcs?: ImageSources;
  imgAltText?: string;
  // video setings
  videoSource?: string;
  mobileVideoSource?: string;
  posterSource?: string;
  captionsSource?: string;
  onTimeUpdate?: ReactEventHandler<HTMLVideoElement>;
  onPlay?: ReactEventHandler<HTMLVideoElement>;
  onPause?: ReactEventHandler<HTMLVideoElement>;
  // carousel controls
  shouldLoad?: boolean;
  isVisible?: boolean;
  isSingleView?: boolean;
  autoplay?: boolean;
  showVideoControls?: boolean;
  isResponsiveWidth?: boolean;
  hideLinkArrow?: boolean;
  logoSrcs?: ImageSources;
  logoAltText?: string;
  relatedContent?: boolean;
}

export const CarouselCardItem: FC<CarouselCardItemProps> = ({
  aspectRatio,
  autoplay,
  body,
  captionsSource,
  imgAltText,
  imgSrcs,
  shouldLoad,
  onPlay,
  onPause,
  onTimeUpdate,
  openNewTab,
  posterSource,
  isVisible,
  showVideoControls,
  subtitle,
  title,
  url,
  videoSource,
  mobileVideoSource,
  onClick,
  isSingleView = false,
  isResponsiveWidth = false,
  hideLinkArrow,
  logoSrcs,
  logoAltText,
  className,
  date,
  brand,
  relatedContent = false,
}) => {
  const [media, setMedia] = useState<HTMLElement>();
  const [isLogoOverlayHovered, setIsLogoOverlayHovered] = useState(false);
  const mediaCallback = useCallback((elt: HTMLElement) => setMedia(elt), []);
  const didLoad = useRef(shouldLoad);
  const { Anchor } = useContext(PrimitivesContext);
  const isMobile = useIsMobile();

  // Only use responsive width style if the carousel is in multi view mode
  const isResponsiveWidthMultiView = isResponsiveWidth && !isSingleView && !isMobile;
  const isTextOverlap = !isResponsiveWidthMultiView && (!videoSource || !showVideoControls);
  const hasLogoOverlay = !isMobile && logoSrcs;

  const onClickWrapped = useCallback<MouseEventHandler>(
    event => {
      url && onClick && onClick(url, event);
    },
    [url, onClick]
  );

  // isVisible indicates if any part of the item is on screen
  // It allows us to lazy load images and pause videos when they leave the screen
  useEffect(() => {
    if (shouldLoad) {
      didLoad.current = true;
    }
  }, [shouldLoad]);

  useEffect(() => {
    if (media && media.tagName === 'VIDEO') {
      const video = media as HTMLVideoElement;

      // Ensure video is paused if there is a logo overlay, and only start playing when hovered
      if (hasLogoOverlay) {
        if (isLogoOverlayHovered) {
          try {
            void video.play();
          } catch (_error) {
            // the video may be outside the document at this point
          }
          video.tabIndex = 0;
        } else {
          if (isVideoPlaying(video)) {
            video.pause();
          }
          video.tabIndex = -1;

          if (!showVideoControls) {
            video.currentTime = 0;
          }
        }

        return;
      }

      // If there is no logo overlay, play/pause the video based on visibility
      if (!isVisible) {
        if (isVideoPlaying(video)) {
          video.pause();
        }
        video.tabIndex = -1;

        if (!showVideoControls) {
          video.currentTime = 0;
        }
      } else {
        if (!showVideoControls) {
          try {
            void video.play();
          } catch (_error) {
            // the video may be outside the document at this point
          }
        }
        video.tabIndex = 0;
      }
    }
  }, [isVisible, showVideoControls, media, hasLogoOverlay, isLogoOverlayHovered]);

  const hasBodyContent = !!(title || subtitle || body);

  const mediaElement = useMemo(() => {
    const mediaCss = cx(
      videoSource ? videoCss : imageCss,
      showVideoControls && hasBodyContent ? videoPlayerControlsCss : null
    );

    return (
      <Media
        showVideoControls={showVideoControls}
        videoSource={videoSource}
        mobileVideoSource={mobileVideoSource}
        posterSource={posterSource}
        onPlay={onPlay}
        onPause={onPause}
        captionsSource={captionsSource}
        isBackgroundVideo={autoplay}
        onTimeUpdate={onTimeUpdate}
        altText={imgAltText}
        imgSrcs={imgSrcs}
        className={mediaCss}
        ref={mediaCallback}
        isDraggable={false}
        autoplay={hasLogoOverlay ? false : undefined}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoSource,
    showVideoControls,
    posterSource,
    onPlay,
    onPause,
    captionsSource,
    autoplay,
    onTimeUpdate,
    mediaCallback,
  ]);

  const mediaContent = (
    <div
      className={cx(
        mediaContainerCss,
        { [image32Css]: aspectRatio === '3:2' },
        { [image11Css]: aspectRatio === '1:1' },
        { [image169Css]: aspectRatio === '16:9' },
        { [imageLoadingCss]: !isVisible && !didLoad.current }
      )}
    >
      {(isVisible || didLoad.current) && mediaElement}
      {hasLogoOverlay && (
        <div
          className={mediaLogoContainerCss}
          onMouseEnter={() => setIsLogoOverlayHovered(true)}
          onMouseLeave={() => setIsLogoOverlayHovered(false)}
        >
          <Picture
            imgSrcs={logoSrcs}
            altText={logoAltText}
            className={mediaLogoPictureCss}
            imgClassName={mediaLogoImageCss}
          />
        </div>
      )}
    </div>
  );

  const bodyContent = (
    <div className={cx(textWrapperCss, { [textWrapperOverlapCss]: isTextOverlap })}>
      <div
        className={cx(textCss, 'sdsm-carousel-card-text', {
          [textOverlapCss]: isTextOverlap,
          [textPlayerControlsCss]: showVideoControls && hasBodyContent,
        })}
      >
        {!!url && !hideLinkArrow && <Icon className={arrowCss} name="arrow-right" />}
        {isMobile && logoSrcs && (
          <Picture
            imgSrcs={logoSrcs}
            altText={logoAltText}
            className={mediaLogoPictureMobileCss}
            imgClassName={mediaLogoImageMobileCss}
          />
        )}
        {(brand || date) && (
          <div className={cx(infoElementCss, metadataContainerCss)}>
            {brand ? (
              <span className={metadataTextCss}>{editorialGalleryBrandNames[brand]}</span>
            ) : null}
            {date ? <span className={metadataTextCss}>{date}</span> : null}
          </div>
        )}
        {title && (
          <h5
            className={cx(infoElementCss, titleCss, { [titleOnlyClampCss]: !subtitle && !body })}
            title={title}
          >
            {title}
          </h5>
        )}
        {subtitle && (
          <h6
            className={cx(infoElementCss, subtitleCss, { [subtitleOnlyClampCss]: !title && !body })}
            title={subtitle}
          >
            {subtitle}
          </h6>
        )}
        {body && (
          <p
            className={cx(infoElementCss, bodyCss, {
              /*
               * Related Content carousel items should have only 2 lines of body text.
               * This is defined by design.
               */
              [relatedContentLineClampCss]: relatedContent,
              [bodyOnlyClampCss]: !title && !subtitle,
            })}
            title={body}
          >
            {body}
          </p>
        )}
      </div>
    </div>
  );

  const commonCss = {
    [cardLandscapeWidthCss]: aspectRatio !== '9:16' && !isSingleView,
    [singleViewLandscapeWidthCss]: aspectRatio !== '9:16' && isSingleView,
    [cardPortraitWidthCss]: aspectRatio === '9:16',
    [cardResponsiveWidthCss]: isResponsiveWidthMultiView,
  };

  if (url) {
    return (
      <Anchor
        href={url}
        {...(openNewTab ? { target: '_blank', rel: 'noopener' } : {})}
        className={cx(cardCss, linkCss, commonCss, className)}
        tabIndex={isVisible ? undefined : -1}
        onClick={onClickWrapped}
        draggable="false"
        data-testid="sdsm-carousel-card"
      >
        {mediaContent}
        {hasBodyContent && bodyContent}
      </Anchor>
    );
  }

  return (
    <div className={cx(cardCss, commonCss, className)} data-testid="sdsm-carousel-card">
      {mediaContent}
      {hasBodyContent && bodyContent}
    </div>
  );
};

CarouselCardItem.displayName = 'CarouselCardItem';
